// The pure half of the market's live feed: how each MarketChannel message folds into the
// JSON the /api/market/* routes answer, and how a trader's portfolio is revalued at the
// live prices. No React and no socket here, so vitest can cover it (live-state.test.ts).
// The message shapes are built by app/services/market_broadcasts.rb.
import type {
  LeaderboardResponse,
  PortfolioResponse,
  StateResponse,
} from '@/types/market';

export type TradeMessage = {
  type: 'trade';
  version: number;
  at: string;
  qVector: Record<string, number>;
  prices: Record<string, number>;
};

export type StateMessage = { type: 'state'; state: StateResponse };

export type LeaderboardMessage = {
  type: 'leaderboard';
  version: number;
  board: Omit<LeaderboardResponse, 'viewerId' | 'version'>;
};

export type MarketMessage = TradeMessage | StateMessage;

// A trade the snapshot has already seen (or one older than it) changes nothing.
export function applyTrade(
  state: StateResponse,
  trade: TradeMessage,
): StateResponse {
  if (state.version >= trade.version) return state;
  return {
    ...state,
    version: trade.version,
    qVector: trade.qVector,
    projects: state.projects.map((p) => ({
      ...p,
      price: trade.prices[p.id] ?? p.price,
    })),
  };
}

// A trade priced a project the snapshot does not list: the tradeable set moved (a team
// committed for the first time), and only a refetch brings the new project's details.
export function hasUnknownProjects(
  state: StateResponse,
  trade: TradeMessage,
): boolean {
  const known = new Set(state.projects.map((p) => p.id));
  return Object.keys(trade.prices).some((id) => !known.has(id));
}

// The broadcast state is the anonymous one: everything about the viewer stays as the
// viewer's own fetch had it. Equal versions still apply — a stage change does not touch
// the market row.
export function applyState(
  state: StateResponse,
  incoming: StateResponse,
): StateResponse {
  if (state.version > incoming.version) return state;
  return {
    ...incoming,
    myBalance: state.myBalance,
    myUserType: state.myUserType,
    canTrade: state.canTrade,
  };
}

// The pushed board is everyone's; the viewer's row is found again by their id.
export function applyLeaderboard(
  current: LeaderboardResponse,
  message: LeaderboardMessage,
): LeaderboardResponse {
  if (current.version > message.version) return current;
  const viewerId = current.viewerId;
  return {
    ...message.board,
    myRow: viewerId
      ? (message.board.rows.find((r) => r.userId === viewerId) ?? null)
      : null,
    viewerId,
    version: message.version,
  };
}

// MarketPortfolio.value at the live prices: each position floored on its own, a
// resolved market paying 1 per winning share and nothing for the rest. Cash, costs and
// recent trades only move with the trader's own trades, which refetch the portfolio.
export function revaluePortfolio(
  portfolio: PortfolioResponse,
  state: StateResponse,
): PortfolioResponse {
  const resolved = state.market.status === 'resolved';
  const winnerId = state.market.winnerProjectId;
  const prices = new Map(state.projects.map((p) => [p.id, p.price]));

  const positions = portfolio.positions.map((position) => {
    const winner = resolved && position.projectId === winnerId;
    const currentPrice = resolved
      ? winner
        ? 1
        : 0
      : (prices.get(position.projectId) ?? 0);
    const valueBananas = winner
      ? position.shares
      : Math.floor(position.shares * currentPrice);
    const pnlBananas = valueBananas - position.totalCostBananas;
    return {
      ...position,
      currentPrice,
      valueBananas,
      pnlBananas,
      pnlPct: percent(pnlBananas, position.totalCostBananas),
    };
  });

  const totalPositionsValue = positions.reduce(
    (sum, p) => sum + p.valueBananas,
    0,
  );
  const totalValueBananas = portfolio.cashBananas + totalPositionsValue;
  const totalPnlBananas = totalValueBananas - portfolio.initialBananas;

  return {
    ...portfolio,
    positions,
    totalPositionsValue,
    totalValueBananas,
    totalPnlBananas,
    totalPnlPct: percent(totalPnlBananas, portfolio.initialBananas),
  };
}

function percent(amount: number, base: number): number {
  return base > 0 ? (amount / base) * 100 : 0;
}
